import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader, SyncButton, AdminEmptyState, ResponsiveTableWrap } from "@/components/admin/crud-helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, PackageMinus, PackagePlus } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/branch-inventory")({
  component: BranchInventoryPage,
});

type Branch = { id: string; name: string; code: string };
type Variant = { id: string; product_id: string; name: string };
type Product = { id: string; name: string };
type InvRow = {
  id: string;
  branch_id: string;
  variant_id: string;
  qty: number;
  low_stock_threshold: number;
  updated_at: string;
};

function BranchInventoryPage() {
  const qc = useQueryClient();
  const [branchId, setBranchId] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [stockFilter, setStockFilter] = useState<"all" | "in" | "low" | "out">("all");

  const branches = useQuery({
    queryKey: ["branches_min"],
    queryFn: async () => {
      const { data, error } = await supabase.from("branches").select("id, name, code").order("name");
      if (error) throw error;
      return (data ?? []) as Branch[];
    },
  });

  const inventory = useQuery({
    queryKey: ["branch_inventory_all"],
    queryFn: async () => {
      const { data, error } = await supabase.from("branch_inventory").select("*").order("updated_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as InvRow[];
    },
  });

  const variants = useQuery({
    queryKey: ["variants_min"],
    queryFn: async () => {
      const { data, error } = await supabase.from("product_variants").select("id, product_id, name");
      if (error) throw error;
      return (data ?? []) as Variant[];
    },
  });

  const products = useQuery({
    queryKey: ["products_min"],
    queryFn: async () => {
      const { data, error } = await supabase.from("products").select("id, name");
      if (error) throw error;
      return (data ?? []) as Product[];
    },
  });

  const branchMap = useMemo(() => new Map((branches.data ?? []).map(b => [b.id, b])), [branches.data]);
  const variantMap = useMemo(() => new Map((variants.data ?? []).map(v => [v.id, v])), [variants.data]);
  const productMap = useMemo(() => new Map((products.data ?? []).map(p => [p.id, p])), [products.data]);

  const filtered = useMemo(() => {
    const rows = inventory.data ?? [];
    return rows.filter(r => {
      if (branchId !== "all" && r.branch_id !== branchId) return false;
      if (stockFilter === "low" && !(r.qty > 0 && r.qty <= r.low_stock_threshold)) return false;
      if (stockFilter === "out" && r.qty > 0) return false;
      if (stockFilter === "in" && !(r.qty > r.low_stock_threshold)) return false;
      if (!search.trim()) return true;
      const v = variantMap.get(r.variant_id);
      const p = v ? productMap.get(v.product_id) : undefined;
      const b = branchMap.get(r.branch_id);
      const hay = `${p?.name ?? ""} ${v?.name ?? ""} ${b?.name ?? ""} ${b?.code ?? ""}`.toLowerCase();
      return hay.includes(search.toLowerCase());
    });
  }, [inventory.data, branchId, search, stockFilter, variantMap, productMap, branchMap]);

  const stats = useMemo(() => {
    const rows = inventory.data ?? [];
    const scoped = branchId === "all" ? rows : rows.filter(r => r.branch_id === branchId);
    const totalUnits = scoped.reduce((s, r) => s + r.qty, 0);
    const low = scoped.filter(r => r.qty > 0 && r.qty <= r.low_stock_threshold).length;
    const out = scoped.filter(r => r.qty <= 0).length;
    return { skus: scoped.length, totalUnits, low, out };
  }, [inventory.data, branchId]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["branch_inventory_all"] });

  const updateRow = async (id: string, patch: Partial<Pick<InvRow, "qty" | "low_stock_threshold">>) => {
    const { error } = await supabase.from("branch_inventory").update({ ...patch, updated_at: new Date().toISOString() }).eq("id", id);
    if (error) toast.error(error.message);
    else invalidate();
  };

  const adjust = async (row: InvRow, delta: number) => {
    const next = Math.max(0, row.qty + delta);
    await updateRow(row.id, { qty: next });
  };

  const remove = async (id: string) => {
    if (!confirm("Remove this inventory record?")) return;
    const { error } = await supabase.from("branch_inventory").delete().eq("id", id);
    if (error) toast.error(error.message);
    else { toast.success("Removed"); invalidate(); }
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Branch inventory"
        description="Update per-branch stock quantities and surface low-stock alerts."
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by product, variant or branch…"
        count={filtered.length}
        actions={
          <>
            <SyncButton onSync={() => inventory.refetch()} loading={inventory.isFetching} />
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/branches">Branches</Link>
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Tracked SKUs" value={stats.skus} />
        <StatCard label="Total units" value={stats.totalUnits} />
        <StatCard label="Low stock" value={stats.low} tone={stats.low > 0 ? "warn" : undefined} />
        <StatCard label="Out of stock" value={stats.out} tone={stats.out > 0 ? "danger" : undefined} />
      </div>

      <AddInventoryCard
        branches={branches.data ?? []}
        variants={variants.data ?? []}
        products={products.data ?? []}
        onAdded={invalidate}
      />

      <div className="flex flex-wrap gap-2">
        <Select value={branchId} onValueChange={setBranchId}>
          <SelectTrigger className="w-[240px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All branches</SelectItem>
            {(branches.data ?? []).map(b => <SelectItem key={b.id} value={b.id}>{b.name} ({b.code})</SelectItem>)}
          </SelectContent>
        </Select>
        {(["all", "in", "low", "out"] as const).map((f) => (
          <Button key={f} size="sm" variant={stockFilter === f ? "default" : "outline"} onClick={() => setStockFilter(f)}>
            {f === "all" ? "All" : f === "in" ? "In stock" : f === "low" ? `Low (${stats.low})` : `Out (${stats.out})`}
          </Button>
        ))}
      </div>

      {!inventory.isLoading && filtered.length === 0 ? (
        <AdminEmptyState title={search || branchId !== "all" || stockFilter !== "all" ? "No inventory matches" : "No inventory records yet"} />
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <ResponsiveTableWrap>
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-xs">
                <tr>
                  <th className="px-4 py-2 text-left">Product / Variant</th>
                  <th className="hidden px-4 py-2 text-left md:table-cell">Branch</th>
                  <th className="px-4 py-2 text-left">Quantity</th>
                  <th className="hidden px-4 py-2 text-left lg:table-cell">Low threshold</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="hidden px-4 py-2 text-left xl:table-cell">Updated</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {inventory.isLoading && <tr><td colSpan={7} className="px-4 py-6 text-muted-foreground">Loading…</td></tr>}
                {filtered.map(r => {
                  const v = variantMap.get(r.variant_id);
                  const p = v ? productMap.get(v.product_id) : undefined;
                  const b = branchMap.get(r.branch_id);
                  const isOut = r.qty <= 0;
                  const isLow = !isOut && r.qty <= r.low_stock_threshold;
                  return (
                    <tr key={r.id} className={
                      isOut ? "border-t border-border bg-destructive/5"
                      : isLow ? "border-t border-border bg-amber-500/5"
                      : "border-t border-border hover:bg-secondary/40"
                    }>
                      <td className="px-4 py-2">
                        <div className="font-medium">{p?.name ?? "—"}</div>
                        <div className="text-xs text-muted-foreground">
                          {v?.name ?? r.variant_id.slice(0, 8)}
                          <span className="md:hidden"> · {b?.code ?? "—"}</span>
                        </div>
                      </td>
                      <td className="hidden px-4 py-2 md:table-cell">
                        {b ? <>{b.name} <Badge variant="outline" className="ml-1">{b.code}</Badge></> : "—"}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => adjust(r, -1)}>
                            <PackageMinus className="h-4 w-4" />
                          </Button>
                          <Input
                            type="number"
                            className="h-8 w-16 text-center sm:w-20"
                            defaultValue={r.qty}
                            key={`${r.id}-${r.qty}`}
                            onBlur={(e) => {
                              const next = Math.max(0, parseInt(e.target.value || "0", 10));
                              if (next !== r.qty) updateRow(r.id, { qty: next });
                            }}
                          />
                          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => adjust(r, 1)}>
                            <PackagePlus className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                      <td className="hidden px-4 py-2 lg:table-cell">
                        <Input
                          type="number"
                          className="h-8 w-20"
                          defaultValue={r.low_stock_threshold}
                          key={`${r.id}-th-${r.low_stock_threshold}`}
                          onBlur={(e) => {
                            const next = Math.max(0, parseInt(e.target.value || "0", 10));
                            if (next !== r.low_stock_threshold) updateRow(r.id, { low_stock_threshold: next });
                          }}
                        />
                      </td>
                      <td className="px-4 py-2">
                        {isOut ? <Badge variant="destructive">Out</Badge>
                          : isLow ? <Badge className="bg-amber-500 text-white hover:bg-amber-500">Low</Badge>
                          : <Badge variant="outline">In stock</Badge>}
                      </td>
                      <td className="hidden px-4 py-2 text-muted-foreground xl:table-cell">{new Date(r.updated_at).toLocaleString()}</td>
                      <td className="px-4 py-2 text-right">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => remove(r.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ResponsiveTableWrap>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: number; tone?: "warn" | "danger" }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className={
          "text-2xl font-semibold mt-1 " +
          (tone === "danger" ? "text-destructive" : tone === "warn" ? "text-amber-500" : "")
        }>{value}</div>
      </CardContent>
    </Card>
  );
}

function AddInventoryCard({
  branches, variants, products, onAdded,
}: { branches: Branch[]; variants: Variant[]; products: Product[]; onAdded: () => void }) {
  const [branchId, setBranchId] = useState("");
  const [variantId, setVariantId] = useState("");
  const [qty, setQty] = useState("0");
  const [threshold, setThreshold] = useState("5");
  const [busy, setBusy] = useState(false);

  const productMap = useMemo(() => new Map(products.map(p => [p.id, p])), [products]);

  const add = async () => {
    if (!branchId || !variantId) { toast.error("Branch and variant required"); return; }
    setBusy(true);
    const { error } = await supabase.from("branch_inventory").insert({
      branch_id: branchId,
      variant_id: variantId,
      qty: Math.max(0, parseInt(qty || "0", 10)),
      low_stock_threshold: Math.max(0, parseInt(threshold || "0", 10)),
    });
    setBusy(false);
    if (error) toast.error(error.message);
    else { toast.success("Inventory added"); setVariantId(""); setQty("0"); onAdded(); }
  };

  return (
    <Card>
      <CardContent className="p-4 grid gap-3 md:grid-cols-[1fr_2fr_120px_140px_auto]">
        <div className="grid gap-2">
          <Label>Branch</Label>
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
            <SelectContent>
              {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name} ({b.code})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label>Product variant</Label>
          <Select value={variantId} onValueChange={setVariantId}>
            <SelectTrigger><SelectValue placeholder="Select variant" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {variants.map(v => {
                const p = productMap.get(v.product_id);
                return <SelectItem key={v.id} value={v.id}>{p?.name ?? "—"} · {v.name}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label>Quantity</Label>
          <Input type="number" value={qty} onChange={e => setQty(e.target.value)} />
        </div>
        <div className="grid gap-2">
          <Label>Low threshold</Label>
          <Input type="number" value={threshold} onChange={e => setThreshold(e.target.value)} />
        </div>
        <div className="flex items-end">
          <Button onClick={add} disabled={busy}>
            <Plus className="mr-2 h-4 w-4" /> Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
